import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Slider,
  Stack,
  useTheme,
  Link,
  Divider
} from '@mui/material';
import { tokens } from "../../theme";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import authService from '../../services/AuthService';
import splinterlandsService from '../../services/SplinterlandsService';
import priceService from '../../services/PriceService';

const BurnSpsModal = ({ open, onClose, onSuccess }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // Auth states
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasKeychain, setHasKeychain] = useState(false);
  
  const [amount, setAmount] = useState(1);
  const [maxAmount, setMaxAmount] = useState(100);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [spsPrice, setSpsPrice] = useState(0);
  const [decPrice, setDecPrice] = useState(0);
  const [estimatedDec, setEstimatedDec] = useState(0);
  const [isGoodValue, setIsGoodValue] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [warningLevel, setWarningLevel] = useState('info'); // 'info', 'warning', 'error'
  const [marketComparisonRatio, setMarketComparisonRatio] = useState(1);
  
  // Subscribe to auth service
  useEffect(() => {
    const authUnsubscribe = authService.subscribe((data) => {
      setIsLoggedIn(data.isLoggedIn);
      setHasKeychain(data.hasKeychain);
    });
    
    return () => {
      authUnsubscribe();
    };
  }, []);
  
  // Load price data and SPS balance
  useEffect(() => {
    const priceUnsubscribe = priceService.subscribe((data) => {
      setSpsPrice(data.prices.sps || 0);
      setDecPrice(data.prices.dec || 0);
    });
    
    // Only subscribe to balance if logged in
    let balanceUnsubscribe = () => {};
    if (isLoggedIn) {
      balanceUnsubscribe = splinterlandsService.subscribe((data) => {
        setMaxAmount(data.balances.sps || 0);
        
        // If amount is greater than available balance, set to maximum balance
        if (amount > data.balances.sps) {
          setAmount(data.balances.sps);
        }
      });
    }
    
    return () => {
      priceUnsubscribe();
      balanceUnsubscribe();
    };
  }, [amount, isLoggedIn]);
  
  // Calculate DEC preview when prices or amount changes
  useEffect(() => {
    if (spsPrice && decPrice && amount) {
      // Calculate the value of SPS in USD
      const spsValueUsd = amount * spsPrice;
      
      // Official formula from Splinterlands: SPS Burned x SPS Market Price x 995 = DEC
      // Changed from 975 (2.5% fee) to 995 (0.5% fee)
      const splinterlandsFormula = amount * spsPrice * 995;
      
      // Calculate how much DEC this USD value would be at market price
      const decAmountMarketValue = spsValueUsd / decPrice;
      
      // Set the estimated DEC to the official formula result
      setEstimatedDec(splinterlandsFormula);
      
      // Check if the burning provides good value compared to market exchanges
      const burnValueRatio = splinterlandsFormula / decAmountMarketValue;
      setMarketComparisonRatio(burnValueRatio);
      
      // Determine warning levels:
      // >= 0.98: Good value (no warning)
      // 0.95-0.98: Caution (info warning)
      // 0.90-0.95: Not great value (warning)
      // < 0.90: Poor value (error warning)
      if (burnValueRatio >= 0.98) {
        setIsGoodValue(true);
        setShowWarning(false);
        setWarningLevel('info');
      } else if (burnValueRatio >= 0.95) {
        setIsGoodValue(true);
        setShowWarning(true);
        setWarningLevel('info');
      } else if (burnValueRatio >= 0.90) {
        setIsGoodValue(false);
        setShowWarning(true);
        setWarningLevel('warning');
      } else {
        setIsGoodValue(false);
        setShowWarning(true);
        setWarningLevel('error');
      }
    }
  }, [spsPrice, decPrice, amount]);
  
  // Slider change handler
  const handleSliderChange = (event, newValue) => {
    setAmount(newValue);
  };
  
  // Text field change handler
  const handleInputChange = (event) => {
    const value = event.target.value === '' ? '' : Number(event.target.value);
    if (value === '') {
      setAmount(0);
    } else if (!isNaN(value) && value >= 0) {
      setAmount(Math.min(value, maxAmount));
    }
  };
  
  // Open login modal
  const handleLoginClick = () => {
    onClose(); // Close the burn modal
    
    // Delay to prevent modal conflicts
    setTimeout(() => {
      // Dispatch an event to open the login modal from the parent
      const event = new CustomEvent('openLoginModal');
      document.dispatchEvent(event);
    }, 300);
  };
  
  // Get appropriate keychain link based on browser
  const getKeychainLink = () => {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    if (isFirefox) {
      return 'https://addons.mozilla.org/en-US/firefox/addon/hive-keychain/';
    }
    return 'https://chrome.google.com/webstore/detail/hive-keychain/jcacnejopjdphbnjgfaaobbfafkihpep';
  };
  
  // Start conversion process
  const handleBurn = async () => {
    if (!amount || amount <= 0) {
      setError('Please enter an amount greater than 0');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Get user from AuthService
      const currentUser = authService.getCurrentUser();
      if (!currentUser || !currentUser.username) {
        throw new Error('User not logged in');
      }
      
      // Use Hive Keychain to convert SPS to DEC
      const burnResult = await convertSpsToDec(currentUser.username, amount);
      
      if (burnResult.success) {
        // Update balances
        await splinterlandsService.refreshBalances();
        
        // Call success callback
        if (onSuccess) {
          onSuccess({
            username: currentUser.username,
            spsBurned: amount,
            decReceived: estimatedDec
          });
        }
        
        // Close modal
        onClose();
      } else {
        setError(burnResult.message || 'An error occurred during the conversion');
      }
    } catch (err) {
      console.error('Error converting SPS to DEC:', err);
      setError(err.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  // Convert SPS to DEC with Hive Keychain - Real blockchain transaction
  const convertSpsToDec = (username, amount) => {
    return new Promise((resolve) => {
      if (!window.hive_keychain) {
        resolve({ success: false, message: 'Hive Keychain is not available' });
        return;
      }
      
      // Format amount correctly
      const formattedAmount = parseFloat(amount).toFixed(3);
      
      // Generate random nonce for transaction
      const nonce = Math.random().toString(36).substring(2, 12);
      
      // Prepare the Splinterlands custom_json for SPS to DEC conversion
      const json = JSON.stringify({
        "token": "SPS",
        "to": "null",
        "qty": parseFloat(formattedAmount),
        "memo": "convert_to_dec",
        "app": "splinterlands/0.7.174",
        "n": nonce
      });
      
      console.log(`Starting SPS to DEC conversion: ${username} converting ${formattedAmount} SPS to DEC`);
      
      // Perform Splinterlands token transfer custom_json operation
      window.hive_keychain.requestCustomJson(
        username,           // User
        "sm_token_transfer", // ID for Splinterlands token transfers
        "Active",           // Required key type
        json,               // JSON data
        "Convert SPS to DEC", // Description for the user
        response => {
          console.log('Keychain response:', response);
          
          if (response.success) {
            resolve({ 
              success: true, 
              message: `${formattedAmount} SPS successfully converted to DEC.`,
              txId: response.result
            });
          } else {
            resolve({ 
              success: false, 
              message: response.message || 'Transaction failed'
            });
          }
        }
      );
    });
  };
  
  // Number formatting
  const formatNumber = (value) => {
    if (!value && value !== 0) return '0';
    // Use 4 decimal places for better precision in price displays
    return value.toLocaleString('en-US', { maximumFractionDigits: 4 });
  };
  
  // Format percentage
  const formatPercent = (value) => {
    return (value * 100).toFixed(1) + '%';
  };

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? onClose : undefined} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { backgroundColor: colors.primary[400] }
      }}
    >
      <DialogTitle sx={{ color: colors.grey[100], display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalFireDepartmentIcon sx={{ color: colors.redAccent[500] }} />
        Convert SPS to DEC
      </DialogTitle>
      
      <DialogContent>
        {!hasKeychain ? (
          // Show Keychain installation instructions
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1">
                Hive Keychain extension is required to burn SPS for DEC.
              </Typography>
            </Alert>
            
            <Typography variant="body2" sx={{ mb: 3 }} color={colors.grey[100]}>
              Hive Keychain is a secure browser extension that allows you to interact with Hive blockchain applications like Splinterlands.
            </Typography>
            
            <Button
              variant="contained"
              color="secondary"
              component={Link}
              href={getKeychainLink()}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ mb: 2 }}
            >
              Install Hive Keychain
            </Button>
          </Box>
        ) : !isLoggedIn ? (
          // Show login prompt
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body1">
                You need to be logged in to convert SPS to DEC.
              </Typography>
            </Alert>
            
            <Typography variant="body2" sx={{ mb: 3 }} color={colors.grey[100]}>
              Please log in with your Hive account to access this feature.
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<PersonOutlinedIcon />}
              onClick={handleLoginClick}
              color="secondary"
              sx={{ mb: 2 }}
            >
              Login with Hive
            </Button>
          </Box>
        ) : (
          // Show burn form for logged in users
          <Box sx={{ my: 2 }}>
            <Typography variant="body1" color={colors.grey[100]}>
              Splintershards (SPS) can be converted to Dark Energy Crystals (DEC) using the formula:
            </Typography>
            
            <Typography variant="body1" color={colors.grey[100]} fontWeight="bold" sx={{ textAlign: 'center', my: 1 }}>
              SPS Converted × SPS Market Price × 995 = DEC
            </Typography>
            
            <Box sx={{ my: 3 }}>
              <Typography variant="subtitle1" color={colors.grey[100]} gutterBottom>
                SPS to convert: {formatNumber(amount)}
              </Typography>
              
              <Stack spacing={2} direction="row" sx={{ mb: 2 }} alignItems="center">
                <Slider
                  value={amount}
                  onChange={handleSliderChange}
                  min={0}
                  max={maxAmount}
                  step={0.1}
                  valueLabelDisplay="auto"
                  disabled={loading}
                  sx={{
                    color: colors.redAccent[400],
                    '& .MuiSlider-thumb': {
                      backgroundColor: colors.redAccent[500],
                    },
                    '& .MuiSlider-track': {
                      backgroundColor: colors.redAccent[600],
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: colors.grey[700],
                    }
                  }}
                />
                <TextField
                  value={amount}
                  onChange={handleInputChange}
                  inputProps={{
                    min: 0,
                    max: maxAmount,
                    step: 0.1,
                    type: 'number'
                  }}
                  disabled={loading}
                  sx={{
                    width: '100px',
                    input: { color: colors.grey[100] },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: colors.grey[700] },
                      '&:hover fieldset': { borderColor: colors.grey[500] },
                      '&.Mui-focused fieldset': { borderColor: colors.redAccent[500] },
                    }
                  }}
                />
              </Stack>
              
              <Typography variant="caption" color={colors.grey[300]}>
                Available SPS: {formatNumber(maxAmount)}
              </Typography>
            </Box>
            
            <Box sx={{ 
              bgcolor: colors.primary[500], 
              borderRadius: 1, 
              p: 2, 
              my: 2,
              border: `1px solid ${colors.primary[600]}` 
            }}>
              <Typography variant="subtitle1" color={colors.grey[100]} gutterBottom>
                Market Prices:
              </Typography>
              
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color={colors.grey[300]}>
                    <strong>SPS Market Price:</strong>
                  </Typography>
                  <Typography variant="body2" color={colors.greenAccent[400]} fontWeight="bold">
                    ${formatNumber(spsPrice)}
                  </Typography>
                </Box>
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color={colors.grey[300]}>
                    <strong>DEC Market Price:</strong>
                  </Typography>
                  <Typography variant="body2" color={colors.redAccent[400]} fontWeight="bold">
                    ${formatNumber(decPrice)}
                  </Typography>
                </Box>
                
                <Divider sx={{ borderColor: colors.grey[700], my: 1 }} />
                
                <Box display="flex" alignItems="center" justifyContent="center">
                  <Box sx={{ textAlign: 'center', width: '100%' }}>
                    <Typography variant="h5" color={isGoodValue ? colors.greenAccent[400] : colors.redAccent[400]} sx={{ fontWeight: 'bold' }}>
                      {formatNumber(amount)} SPS
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 1 }}>
                      <CompareArrowsIcon 
                        color={isGoodValue ? 'success' : 'warning'}
                        sx={{ mx: 1 }}
                      />
                    </Box>
                    <Typography variant="h5" color={isGoodValue ? colors.greenAccent[400] : colors.redAccent[400]} sx={{ fontWeight: 'bold' }}>
                      {formatNumber(estimatedDec)} DEC
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ borderColor: colors.grey[700], my: 1 }} />
                
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color={colors.grey[300]}>
                    <strong>Value Comparison:</strong>
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    color={
                      marketComparisonRatio >= 0.98 ? colors.greenAccent[400] : 
                      marketComparisonRatio >= 0.95 ? colors.blueAccent[300] : 
                      marketComparisonRatio >= 0.90 ? colors.redAccent[300] : 
                      colors.redAccent[400]
                    }
                  >
                    {formatPercent(marketComparisonRatio)} of market value
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            {showWarning && (
              <Alert 
                severity={warningLevel}
                sx={{ 
                  mt: 2, 
                  backgroundColor: warningLevel === 'error' 
                    ? 'rgba(211, 47, 47, 0.1)' 
                    : warningLevel === 'warning' 
                      ? 'rgba(237, 108, 2, 0.1)' 
                      : 'rgba(2, 136, 209, 0.1)', 
                  color: warningLevel === 'error' 
                    ? colors.redAccent[400] 
                    : warningLevel === 'warning' 
                      ? colors.redAccent[300] 
                      : colors.blueAccent[400],
                  '.MuiAlert-icon': { 
                    color: warningLevel === 'error' 
                      ? colors.redAccent[400] 
                      : warningLevel === 'warning' 
                        ? colors.redAccent[300] 
                        : colors.blueAccent[400] 
                  }
                }}
                icon={warningLevel === 'info' ? <InfoIcon /> : <WarningAmberIcon />}
              >
                {warningLevel === 'error' ? (
                  <Typography variant="body2">
                    <strong>Not Recommended!</strong> At current prices, you would receive significantly less value by converting SPS. 
                    Consider using <Link href="https://tribaldex.com" target="_blank" rel="noopener" sx={{ color: colors.greenAccent[400] }}>Tribaldex</Link> or 
                    <Link href="https://beeswap.dcity.io/" target="_blank" rel="noopener" sx={{ ml: 1, color: colors.greenAccent[400] }}>BeeSwap</Link> to 
                    trade your SPS for DEC at better rates.
                  </Typography>
                ) : warningLevel === 'warning' ? (
                  <Typography variant="body2">
                    <strong>Better Value Available:</strong> At current market prices, you might get better value by trading your SPS for DEC on exchanges like 
                    <Link href="https://tribaldex.com" target="_blank" rel="noopener" sx={{ mx: 1, color: colors.greenAccent[400] }}>TribalDex</Link> 
                    or 
                    <Link href="https://beeswap.dcity.io/" target="_blank" rel="noopener" sx={{ mx: 1, color: colors.greenAccent[400] }}>BeeSwap</Link>.
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    <strong>Note:</strong> The DEC you'll receive is slightly less than market value. For larger amounts, consider checking 
                    <Link href="https://tribaldex.com" target="_blank" rel="noopener" sx={{ mx: 1, color: colors.greenAccent[400] }}>TribalDex</Link> 
                    rates first.
                  </Typography>
                )}
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ color: colors.grey[300] }}
        >
          Cancel
        </Button>
        
        {hasKeychain && isLoggedIn && (
          <Button 
            onClick={handleBurn}
            variant="contained"
            startIcon={<LocalFireDepartmentIcon />}
            disabled={loading || amount <= 0 || amount > maxAmount}
            sx={{ 
              bgcolor: colors.redAccent[600],
              '&:hover': { bgcolor: colors.redAccent[700] },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirm Conversion'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BurnSpsModal;